// src/components/gavion/domain/dialer/outcomeEffects.ts
// TS mirror of the synchronous part of SQL log_call_outcome (spec 04 §3). Used by the fake API and tests.
import { notInterestedCooldownUntil } from "../cooldown";
import {
  DEFAULT_LEAD_STATUSES,
  isCustomLeadStatus,
  type LeadStatusDef,
} from "../leads/leadStatusModel";
import { DIAL_OUTCOMES, type OutcomePayload } from "./outcomes";
import type { CallOutcome, ProspectStatus } from "./types";

export type CompanyOutcomeFields = {
  attempt_count: number;
  prospect_status: ProspectStatus | string;
  next_callback_at: string | null;
  cooldown_until: string | null;
  phone_valid: boolean;
  phone_e164: string | null;
  email: string | null;
};
export type EffectsInput = {
  outcome: CallOutcome;
  payload: OutcomePayload;
  company: CompanyOutcomeFields;
  previousOutcome: CallOutcome | null;
  dialedAt: string;
  now: Date;
  hasCdr: boolean;
  /**
   * GAVION contact-call: the number dialled when the call went to a contact (calls.contact_id set), else null.
   * SQL twin: v_contact_call in log_call_outcome (39_gavion_contact_call.sql).
   */
  contactPhone?: string | null;
  /**
   * Follow-up M5: the call went to a contact (calls.contact_id set), whether or not its number is still on the row.
   * SQL keys on contact_id alone (v_contact_call); defaults to !!contactPhone for callers that only pass the number.
   */
  contactCall?: boolean;
  /** GAVION lead statuses: the configured list (an added status is kept on a soft outcome). Absent: the defaults. */
  statuses?: readonly LeadStatusDef[];
};
export type OutcomeEffects = {
  companyPatch: Record<string, unknown>;
  session: { dials: number; contacts: number; demos: number };
  suppress: { channel: "phone" | "email"; value: string }[];
  releaseLease: boolean;
  deleteCall: boolean;
  demoDealPrompt: boolean;
};

/** Ruling: these outcomes clear a callback that is already due (<= now); a future callback is kept. */
const CLEARS_DUE_CALLBACK: readonly CallOutcome[] = [
  "no_answer",
  "voicemail",
  "brochure_sent",
];
/**
 * Review fix I3 (SQL twin: v_soft / v_keep_status in log_call_outcome, 39_gavion_contact_call.sql): a soft outcome
 * says nothing about the lead's progress, so it never changes prospect_status on a customer or demo_booked lead, nor
 * moves a lead out of bad_number unless the call was on the main line. Status-setting outcomes are unaffected.
 */
const SOFT: readonly CallOutcome[] = [
  "no_answer",
  "voicemail",
  "brochure_sent",
  "unlogged",
];
const KEPT_STATUSES: readonly string[] = [
  "customer",
  "demo_booked",
  "in_pipeline", // GAVION call-brief-pipeline (log_call_outcome, 83_)
];
const isContactCall = (i: EffectsInput): boolean =>
  i.contactCall ?? !!i.contactPhone;
const keepsStatus = (i: EffectsInput): boolean => {
  const contactCall = isContactCall(i);
  const soft =
    SOFT.includes(i.outcome) || (i.outcome === "wrong_number" && contactCall);
  const status = String(i.company.prospect_status);
  return (
    soft &&
    (KEPT_STATUSES.includes(status) ||
      (status === "bad_number" && contactCall) ||
      isCustomLeadStatus(i.statuses ?? DEFAULT_LEAD_STATUSES, status)) // GAVION lead statuses
  );
};
const isDue = (iso: string | null, now: Date): boolean =>
  iso !== null && Date.parse(iso) <= now.getTime();

export function outcomeEffects(i: EffectsInput): OutcomeEffects {
  const first = i.previousOutcome === null || i.previousOutcome === "unlogged";
  const none = { dials: 0, contacts: 0, demos: 0 };
  const demoDealPrompt =
    i.previousOutcome === "demo_booked" && i.outcome !== "demo_booked";
  if (i.outcome === "skipped") {
    const deleteCall = first && !i.hasCdr;
    return {
      companyPatch: {},
      session: { ...none, dials: deleteCall ? -1 : 0 },
      suppress: [],
      releaseLease: true,
      deleteCall,
      demoDealPrompt,
    };
  }
  const patch: Record<string, unknown> = {
    last_called_at: i.dialedAt,
    last_call_outcome: i.outcome,
  };
  if (first) patch.attempt_count = i.company.attempt_count + 1;
  const suppress: OutcomeEffects["suppress"] = [];
  if (
    CLEARS_DUE_CALLBACK.includes(i.outcome) &&
    isDue(i.company.next_callback_at, i.now)
  )
    patch.next_callback_at = null;
  switch (i.outcome) {
    case "no_answer":
    case "voicemail":
    case "unlogged":
      patch.prospect_status = "in_progress";
      break;
    case "talked_callback":
      patch.prospect_status = "callback";
      patch.next_callback_at = i.payload.callback_at ?? null;
      break;
    case "demo_booked":
      patch.prospect_status = "demo_booked";
      patch.next_callback_at = null;
      break;
    case "brochure_sent":
      patch.prospect_status = "in_progress";
      if (!i.company.email && i.payload.email)
        patch.email = i.payload.email.trim().toLowerCase();
      break;
    case "not_interested":
      patch.prospect_status = "not_interested";
      patch.next_callback_at = null;
      patch.cooldown_until = notInterestedCooldownUntil(i.now);
      break;
    case "wrong_number":
      if (isContactCall(i)) {
        // a wrong contact number says nothing about the lead's line: like no_answer
        patch.prospect_status = "in_progress";
        if (isDue(i.company.next_callback_at, i.now))
          patch.next_callback_at = null;
        break;
      }
      patch.prospect_status = "bad_number";
      patch.phone_valid = false;
      patch.next_callback_at = null;
      break;
    case "do_not_call":
      patch.prospect_status = "do_not_call";
      patch.next_callback_at = null;
      if (i.company.phone_e164)
        suppress.push({ channel: "phone", value: i.company.phone_e164 });
      if (i.company.email)
        suppress.push({
          channel: "email",
          value: i.company.email.toLowerCase(),
        });
      if (i.contactPhone)
        suppress.push({ channel: "phone", value: i.contactPhone });
      break;
  }
  if (keepsStatus(i)) delete patch.prospect_status;
  const def = DIAL_OUTCOMES.find((o) => o.code === i.outcome);
  const session = first
    ? {
        dials: 0,
        contacts: def?.reachedHuman ? 1 : 0,
        demos: i.outcome === "demo_booked" ? 1 : 0,
      }
    : none;
  return {
    companyPatch: patch,
    session,
    suppress,
    releaseLease: true,
    deleteCall: false,
    demoDealPrompt,
  };
}
